import React, { Children, HTMLAttributes, ReactElement, cloneElement, forwardRef, isValidElement } from "react";
import classNames from "classnames";

import { composeRefs } from "./refs";

type AnyProps = Record<string, unknown>;

/** React event props look like onClick, onKeyDown, etc. */
function isEventProp(name: string) {
  return /^on[A-Z]/.test(name);
}

/**
 * Merge Slot props onto the child. Child wins on conflicts, except we compose
 * event handlers (child first) and join className/style.
 */
function mergeProps(slotProps: AnyProps, childProps: AnyProps): AnyProps {
  const overrideProps: AnyProps = { ...childProps };

  for (const propName in childProps) {
    const slotValue = slotProps[propName];
    const childValue = childProps[propName];

    if (isEventProp(propName)) {
      if (typeof slotValue === "function" && typeof childValue === "function") {
        overrideProps[propName] = (...args: unknown[]) => {
          (childValue as (...a: unknown[]) => void)(...args);
          (slotValue as (...a: unknown[]) => void)(...args);
        };
      } else if (typeof slotValue === "function") {
        overrideProps[propName] = slotValue;
      }
    } else if (propName === "style") {
      overrideProps[propName] = {
        ...(typeof slotValue === "object" && slotValue !== null ? slotValue : {}),
        ...(typeof childValue === "object" && childValue !== null ? childValue : {}),
      };
    } else if (propName === "className") {
      overrideProps[propName] = classNames(
        typeof slotValue === "string" ? slotValue : undefined,
        typeof childValue === "string" ? childValue : undefined,
      );
    }
  }

  return { ...slotProps, ...overrideProps };
}

export type SlotProps = HTMLAttributes<HTMLElement> & {
  children?: React.ReactNode;
};

/**
 * Renders the single child element instead of a wrapper, cloning Slot props
 * onto it. Used by `asChild` so a custom button can own the DOM node.
 */
export const Slot = forwardRef<HTMLElement, SlotProps>(({ children, ...slotProps }, forwardedRef) => {
  const child = Children.only(children);

  if (!isValidElement(child)) {
    return null;
  }

  const childElement = child as ReactElement & { ref?: React.Ref<HTMLElement> };
  const merged = mergeProps(slotProps as AnyProps, (childElement.props || {}) as AnyProps);

  return cloneElement(childElement, {
    ...merged,
    ref: composeRefs(forwardedRef, childElement.ref),
  });
});

Slot.displayName = "Slot";
